// "Access data & eRound": the one onboarding step between the signed contract
// and the promotion, as both the progress tab and the next-step bar tell it.
//
// It used to be told twice, differently: the journey had a "Credentials
// generated" row followed by one eRound row per station, while the next-step
// bar knew nothing of eRound and said "promote" while a station was still
// waiting for approval. Both now read this, so they cannot disagree.

import { StationProvisioning } from "../hooks/useStationProvisioning";
import { JourneyStep, JourneyStepItem, JourneyStepStatus, OnboardingState, StateTransition } from "../types/onboarding";
import { canPromoteToCustomer, canRegisterStations, pipelineIndex } from "./onboardingStates";
import { manualActionGuidance, missingLabel, missingWaitingOn } from "./eroundProvisioning";

// "setup-eround" opens the one approval that confirms the access data, promotes
// the customer and sends the stations to eRound (CWA-231).
export type StationSetupAction = "setup-eround" | "review-eround";

export const SETUP_EROUND_LABEL = "Set up in eRound + OCPP";

// Where an unmapped wallbox model gets its eRound names.
export const WALLBOX_MODELS_PATH = "/onboarding/eround-provisioning?tab=models";

export interface StationSetupNext {
  description: string;
  action: StationSetupAction | null;
  label: string | null;
  waiting: boolean;
  // The station the action is about, for "review-eround".
  stationId: string | null;
  // A page elsewhere that fixes what the station waits on, instead of an action here.
  href?: string | null;
}

export interface StationSetup {
  status: JourneyStepStatus;
  completedAt: string | null;
  items: JourneyStepItem[];
  // Only while the prospect is in this step; null before and after it.
  next: StationSetupNext | null;
  // Once promoted, what eRound still needs before the configuration email (CWA-231); null when nothing.
  afterPromotion: StationSetupNext | null;
}

export const STATION_SETUP_LABEL = "Access data & eRound";

const SIGNED = pipelineIndex("CONTRACT_SIGNED");
const ACCESS_DATA = pipelineIndex("OCPP_CREDENTIALS_GENERATED");

/** The prospect is in this step: signed, not yet promoted. */
const inStep = (state: OnboardingState) => canRegisterStations(state) || canPromoteToCustomer(state);

type Station = { id: string; entry: StationProvisioning | undefined };

const accessItem = (state: OnboardingState, transitions: StateTransition[]): JourneyStepItem => {
  const recorded = transitions.find((t) => t.state === "OCPP_CREDENTIALS_GENERATED");
  if (recorded) {
    return {
      label: "Access data confirmed",
      status: "done",
      at: recorded.transitioned_at,
      note: null,
      actor: recorded.triggered_by ?? null,
      actor_role: recorded.triggered_by_role ?? null,
    };
  }
  const blank = { at: null, note: null, actor: null, actor_role: null };
  if (canRegisterStations(state)) return { ...blank, label: "Access data not confirmed yet", status: "current" };
  if (state === "OCPP_CREDENTIALS_GENERATED") return { ...blank, label: "Access data confirmed", status: "done" };
  return pipelineIndex(state) > ACCESS_DATA
    ? { ...blank, label: "Access data", status: "skipped", note: "Not recorded — the prospect moved past it." }
    : { ...blank, label: "Access data", status: "pending" };
};

const stationItem = ({ id, entry }: Station, many: boolean): JourneyStepItem => {
  const named = (text: string) => (many ? `${id}: ${text}` : `eRound: ${text}`);
  const item = (label: string, status: JourneyStepStatus, at: string | null = null, note: string | null = null, actor: string | null = null) => ({
    label: named(label),
    status,
    at,
    note,
    actor,
    actor_role: actor ? ("OPS" as const) : status === "done" || status === "failed" ? ("SYSTEM" as const) : null,
  });
  if (!entry || entry.state === "loading") return item("checking…", "pending");
  if (entry.state === "unavailable") return item("status could not be loaded", "pending");
  const record = entry.record;
  // Not tracked: the station predates the automation, or was never sent to it.
  if (!record) return item("not tracked — set it up in eRound by hand", "skipped");
  const finished = record.lastResult?.finishedAt ?? record.updatedAt;
  switch (record.status) {
    case "SUCCEEDED":
      return item("created in eRound", "done", finished);
    case "FAILED":
      return item("failed", "failed", finished, record.lastResult?.reason ?? "No reason given.");
    case "NEEDS_MANUAL_ACTION": {
      // Support marked the step as done in the requests list (SOPS-196).
      const handled = record.manualActionHandled;
      return handled
        ? item("manual step done", "done", handled.at, null, handled.actorEmail ?? handled.actorId)
        : item("needs a manual step", "failed", record.updatedAt, manualActionGuidance(record.manualActionReason));
    }
    case "DISPATCHED":
    case "IN_PROGRESS":
      return item("being created", "current", record.approval?.at ?? record.updatedAt, null, record.approval?.actorEmail ?? record.approval?.actorId ?? null);
    case "AWAITING_APPROVAL":
      return item("ready for approval", "current");
    case "COLLECTING":
      return item("waiting for data", "pending", null, `Missing: ${record.missing.map(missingLabel).join("; ")}.`);
    case "REJECTED":
      return item("set up by hand", "skipped", record.rejection?.at ?? null, record.rejection?.reason ? `Rejected: ${record.rejection.reason}` : "Rejected.", record.rejection?.actorEmail ?? record.rejection?.actorId ?? null);
    default:
      // CANCELLED / SUPERSEDED: nothing left to do for this station.
      return item("withdrawn", "skipped", record.updatedAt);
  }
};

const statusOf = (s: Station) => (s.entry?.state === "loaded" ? s.entry.record?.status ?? "UNTRACKED" : s.entry?.state ?? "loading");

const firstWith = (stations: Station[], statuses: string[]) => stations.find((s) => statuses.includes(statusOf(s)));

const handled = (s: Station) => s.entry?.state === "loaded" && Boolean(s.entry.record?.manualActionHandled);

const recordOf = (s: Station) => (s.entry?.state === "loaded" ? s.entry.record : null);

const step = (
  description: string,
  action: StationSetupAction | null,
  label: string | null,
  stationId: string | null = null,
  waiting = false,
  href: string | null = null
): StationSetupNext => ({ description, action, label, stationId, waiting, href });

// The pieces of a collecting station only support can supply (SOPS-165).
const supportGaps = (s: Station): string[] =>
  (recordOf(s)?.missing ?? []).filter((piece) => missingWaitingOn(piece) === "SUPPORT");

const waitsOnSupport = (s: Station) => statusOf(s) === "COLLECTING" && supportGaps(s).length > 0;

const join = (parts: string[]) =>
  parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;

/**
 * What eRound needs from support or the customer before anything can be approved,
 * shared by the step before and after the promotion. Null when nothing blocks.
 */
const blockerFor = (stations: Station[], many: boolean): StationSetupNext | null => {
  const who = (s: Station) => (many ? ` for ${s.id}` : "");
  const failed = firstWith(stations, ["FAILED"]);
  if (failed) {
    return step(`eRound setup failed${who(failed)}: ${recordOf(failed)?.lastResult?.reason ?? "no reason given"}`, "review-eround", "See failure & retry", failed.id);
  }
  const manual = stations.find((s) => statusOf(s) === "NEEDS_MANUAL_ACTION" && !handled(s));
  if (manual) {
    return step(manualActionGuidance(recordOf(manual)?.manualActionReason ?? null), "review-eround", "See what to do", manual.id);
  }
  const support = stations.find(waitsOnSupport);
  if (support) {
    const record = recordOf(support);
    // The wallbox model is the common one, and it is fixed on its own page.
    if (supportGaps(support).includes("MODEL_NOT_MAPPED")) {
      const model = [record?.station?.manufacturer, record?.station?.model].filter(Boolean).join(" ") || "the wallbox";
      return step(`Waiting for support${who(support)}: map ${model} to an eRound model.`, null, "Map the wallbox", support.id, false, WALLBOX_MODELS_PATH);
    }
    return step(`Waiting for support${who(support)}: ${supportGaps(support).map(missingLabel).join("; ")}.`, "review-eround", "See what's missing", support.id);
  }
  const collecting = firstWith(stations, ["COLLECTING"]);
  if (collecting) {
    const missing = (recordOf(collecting)?.missing ?? []).map(missingLabel).join("; ");
    return step(`eRound setup${who(collecting)} is waiting for the customer: ${missing}.`, null, null, collecting.id, true);
  }
  return null;
};

const nextFor = (state: OnboardingState, stations: Station[], many: boolean): StationSetupNext => {
  const blocker = blockerFor(stations, many);
  if (blocker) return blocker;
  if (firstWith(stations, ["loading"])) return step("Checking eRound…", null, null, null, true);

  const approving = stations.filter((s) => statusOf(s) === "AWAITING_APPROVAL");
  const byHand = stations.filter((s) => ["REJECTED", "UNTRACKED", "unavailable"].includes(statusOf(s)));
  const parts = [
    ...(canRegisterStations(state) ? ["confirm the access data"] : []),
    "promote the customer",
    ...(approving.length ? [`send ${approving.length === 1 ? "the station" : `${approving.length} stations`} to eRound`] : []),
  ];
  const sentence = join(parts);
  const handBuilt = byHand.length ? ` ${byHand.map((s) => s.id).join(", ")} ${byHand.length === 1 ? "is" : "are"} set up in eRound by hand.` : "";
  return step(`${sentence[0].toUpperCase()}${sentence.slice(1)} — one approval.${handBuilt}`, "setup-eround", SETUP_EROUND_LABEL);
};

// After the promotion the configuration email waits for eRound to have every station.
const afterPromotionFor = (stations: Station[], many: boolean): StationSetupNext | null => {
  const blocker = blockerFor(stations, many);
  if (blocker) return blocker;
  const approving = stations.filter((s) => statusOf(s) === "AWAITING_APPROVAL");
  if (approving.length) {
    const what = approving.length === 1 ? "the station" : `${approving.length} stations`;
    return step(`Send ${what} to eRound — one approval.`, "setup-eround", SETUP_EROUND_LABEL);
  }
  const tracked = stations.filter((s) => recordOf(s) !== null);
  const running = tracked.filter((s) => ["DISPATCHED", "IN_PROGRESS"].includes(statusOf(s)));
  if (!running.length) return null;
  const done = tracked.filter((s) => statusOf(s) === "SUCCEEDED").length;
  const progress = many ? ` ${done} of ${tracked.length} stations done.` : "";
  return step(`Being set up in eRound.${progress}`, null, null, running[0].id, true);
};

export const stationSetup = ({
  currentState,
  transitions,
  stationIds,
  byStation,
}: {
  currentState: OnboardingState;
  transitions: StateTransition[];
  stationIds: string[];
  byStation: Record<string, StationProvisioning>;
}): StationSetup => {
  const stations: Station[] = stationIds.map((id) => ({ id, entry: byStation[id] }));
  const many = stations.length > 1;
  const parts = [accessItem(currentState, transitions), ...stations.map((s) => stationItem(s, many))];
  const ended = currentState === "RESIGNED";
  const reached = ended ? parts.some((i) => i.status === "done") : pipelineIndex(currentState) >= SIGNED;
  if (!reached) {
    // Nothing to say about the parts of a step the prospect never got to.
    return { status: ended ? "not-reached" : "pending", completedAt: null, items: [], next: null, afterPromotion: null };
  }

  const finished = parts.every((i) => i.status === "done" || i.status === "skipped");
  let status: JourneyStepStatus;
  if (parts.some((i) => i.status === "failed")) status = "failed";
  else if (finished) status = parts.some((i) => i.status === "done") ? "done" : "skipped";
  else if (inStep(currentState)) status = "current";
  // Promoted while a station was still on its way: the lines say where it is.
  else status = "pending";

  const latest = parts.reduce<string | null>((max, i) => (i.at && (!max || i.at > max) ? i.at : max), null);
  return {
    status,
    completedAt: status === "done" ? latest : null,
    items: parts,
    next: inStep(currentState) ? nextFor(currentState, stations, many) : null,
    afterPromotion: currentState === "CUSTOMER_PROMOTED" ? afterPromotionFor(stations, many) : null,
  };
};

/**
 * The journey with "Access data & eRound" in place of the access-data state.
 * While the prospect is in it, the row it was signed on is behind it, and once
 * it is done, promoting is the step the prospect is on.
 */
export const withStationSetup = (steps: JourneyStep[], setup: StationSetup): JourneyStep[] => {
  const at = steps.findIndex((s) => s.state === "OCPP_CREDENTIALS_GENERATED");
  if (at < 0) return steps;
  const here = setup.next !== null;
  // A note on the row the prospect was on (more info needed, say) stays with it.
  const moved = here ? steps.slice(0, at + 1).find((s) => s.status === "current")?.note ?? null : null;
  return steps.map((step, i) => {
    if (i === at) {
      return {
        ...step,
        state: "STATION_SETUP",
        label: STATION_SETUP_LABEL,
        status: setup.status,
        completed_at: setup.completedAt,
        note: moved,
        actor: null,
        actor_role: null,
        items: setup.items,
      };
    }
    if (!here) return step;
    if (i < at && step.status === "current") return { ...step, status: "done", note: null };
    if (step.state === "CUSTOMER_PROMOTED" && setup.status === "done") return { ...step, status: "current" };
    return step;
  });
};
